/**
 * 登録 / 更新 / 削除の確認ダイアログを画面から呼び出すためのフック (Issue #765)。
 *
 * 確認の種類:
 *   - `confirmExecute`   「実行してよろしいですか」(削除など、取り消しのきかない操作)
 *   - `confirmDiscard`   「変更内容は破棄されますが宜しいですか」(登録 / 更新せずに閉じるとき)
 *   - `confirmOverwrite` 「他者が更新していますが、更新してもよろしいでしょうか」
 *   - `noticeDeleted`    「変更したデータは、他者によって削除されております」(OK のみのお知らせ)
 *   - `guardedUpdate`    更新確定時に、他者の更新 / 削除を確認してから実行する共通処理
 *
 * 使い方:
 *   const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();
 *   ...
 *   {confirmDialog}   // JSX のどこか (モーダルの内側でもよい) に 1 回だけ置く
 *
 * 確認ダイアログ自体は既存の `ConfirmDialog` / `NoticeDialog` (背景クリック / ESC では閉じない) を使う。
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import { ConfirmDialog } from '../components/ConfirmDialog';
import { NoticeDialog } from '../components/NoticeDialog';
import {
  CONFIRM_DISCARD_LABEL,
  CONFIRM_DISCARD_MESSAGE,
  CONFIRM_DISCARD_TITLE,
  CONFIRM_EXECUTE_LABEL,
  CONFIRM_EXECUTE_MESSAGE,
  CONFIRM_EXECUTE_TITLE,
  CONFIRM_OVERWRITE_LABEL,
  CONFIRM_OVERWRITE_MESSAGE,
  CONFIRM_OVERWRITE_TITLE,
  NOTICE_DELETED_BY_OTHERS_MESSAGE,
  NOTICE_DELETED_BY_OTHERS_TITLE,
} from '../constants/confirmMessages';
import type { RemoteChangeState } from '../utils/remoteChange';

interface PendingConfirm {
  title: string;
  message: string;
  confirmLabel: string;
  destructive: boolean;
  onConfirm: () => void;
}

/** 「他者によって削除されています」のお知らせを閉じたあとの処理 */
interface PendingNotice {
  onClose: (() => void) | undefined;
}

/** {@link UseConfirmDialogResult.guardedUpdate} の引数 */
export interface GuardedUpdateOptions {
  /**
   * 本人が何か変更しているか。false のときは他者の更新 / 削除を確認せず、そのまま `execute` する
   * (「自分が何も変更を加えていなければ、メッセージは不要」)。
   */
  hasChanges: boolean;
  /** サーバから最新のデータを取り直し、開いたときと比べた結果を返す。 */
  fetchRemoteState: () => Promise<RemoteChangeState>;
  /** 実際の更新処理。 */
  execute: () => void;
  /** 他者に削除されていたとき、お知らせを閉じた後に呼ぶ (編集を閉じて一覧を再取得する等)。 */
  onDeleted?: () => void;
}

export interface UseConfirmDialogResult {
  /** 「実行してよろしいですか？」を表示し、OK なら `onConfirm` を呼ぶ。 */
  confirmExecute: (onConfirm: () => void) => void;
  /** 「変更内容は破棄されますが宜しいですか？」を表示し、OK なら `onConfirm` を呼ぶ。 */
  confirmDiscard: (onConfirm: () => void) => void;
  /** 「他者が更新していますが、更新してもよろしいでしょうか？」を表示し、OK なら `onConfirm` を呼ぶ。 */
  confirmOverwrite: (onConfirm: () => void) => void;
  /** 「変更したデータは、他者によって削除されております」を表示し、OK で閉じた後に `onClose` を呼ぶ。 */
  noticeDeleted: (onClose?: () => void) => void;
  /**
   * 更新確定の共通処理。本人の変更があるときだけ、実行前に他者の更新 / 削除を確認する。
   *
   *   - 他者が削除 → お知らせを出して実行しない (`onDeleted`)
   *   - 他者が更新 → 確認を出し、OK のときだけ実行する
   *   - 変更なし     → ここでは何も出さずそのまま実行する
   *
   * 最新状態の取得に失敗したときは確認を諦めて実行する (実行側の API が同じ原因で失敗し、エラー表示される)。
   * 確認中の二重呼び出しは無視する。
   */
  guardedUpdate: (options: GuardedUpdateOptions) => Promise<void>;
  /** 描画するダイアログ (JSX のどこかに 1 回だけ置く)。 */
  confirmDialog: React.JSX.Element;
}

export function useConfirmDialog(): UseConfirmDialogResult {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [deletedNotice, setDeletedNotice] = useState<PendingNotice | null>(null);
  /** `guardedUpdate` の最新状態確認中フラグ (二重呼び出し防止) */
  const guardingRef = useRef(false);

  const confirmExecute = useCallback((onConfirm: () => void): void => {
    setPending({
      title: CONFIRM_EXECUTE_TITLE,
      message: CONFIRM_EXECUTE_MESSAGE,
      confirmLabel: CONFIRM_EXECUTE_LABEL,
      destructive: false,
      onConfirm,
    });
  }, []);

  const confirmDiscard = useCallback((onConfirm: () => void): void => {
    setPending({
      title: CONFIRM_DISCARD_TITLE,
      message: CONFIRM_DISCARD_MESSAGE,
      confirmLabel: CONFIRM_DISCARD_LABEL,
      destructive: true,
      onConfirm,
    });
  }, []);

  const confirmOverwrite = useCallback((onConfirm: () => void): void => {
    setPending({
      title: CONFIRM_OVERWRITE_TITLE,
      message: CONFIRM_OVERWRITE_MESSAGE,
      confirmLabel: CONFIRM_OVERWRITE_LABEL,
      destructive: false,
      onConfirm,
    });
  }, []);

  const noticeDeleted = useCallback((onClose?: () => void): void => {
    setDeletedNotice({ onClose });
  }, []);

  const guardedUpdate = useCallback(
    async (options: GuardedUpdateOptions): Promise<void> => {
      const { hasChanges, fetchRemoteState, execute, onDeleted } = options;
      if (!hasChanges) {
        execute();
        return;
      }
      if (guardingRef.current) return;
      guardingRef.current = true;
      let state: RemoteChangeState;
      try {
        state = await fetchRemoteState();
      } catch {
        // 最新状態を確認できないときは確認を諦め、実行側に任せる (同じ原因ならそちらでエラー表示される)。
        state = 'unchanged';
      } finally {
        guardingRef.current = false;
      }
      if (state === 'deleted') {
        noticeDeleted(onDeleted);
        return;
      }
      if (state === 'modified') {
        confirmOverwrite(execute);
        return;
      }
      execute();
    },
    [confirmOverwrite, noticeDeleted],
  );

  const confirmDialog = useMemo(
    () => (
      <>
        <ConfirmDialog
          open={pending !== null}
          title={pending?.title}
          message={pending?.message ?? ''}
          confirmLabel={pending?.confirmLabel}
          destructive={pending?.destructive ?? false}
          onConfirm={() => {
            const action = pending?.onConfirm;
            // 先に閉じてから実行する (実行側が別のダイアログを開いても消されないようにする)。
            setPending(null);
            action?.();
          }}
          onCancel={() => setPending(null)}
        />
        <NoticeDialog
          open={deletedNotice !== null}
          title={NOTICE_DELETED_BY_OTHERS_TITLE}
          message={NOTICE_DELETED_BY_OTHERS_MESSAGE}
          onClose={() => {
            const action = deletedNotice?.onClose;
            setDeletedNotice(null);
            action?.();
          }}
        />
      </>
    ),
    [pending, deletedNotice],
  );

  return {
    confirmExecute,
    confirmDiscard,
    confirmOverwrite,
    noticeDeleted,
    guardedUpdate,
    confirmDialog,
  };
}
